'use client'

import { useState } from 'react'
import {
  Phone, X, FileText, ShoppingCart, Plus, MapPin, ChevronDown, ChevronUp,
  ArrowLeft, Minus, CheckCircle, Home, Coffee, Building2, Users, Settings,
  LogOut, Menu, Receipt, Calculator, Bot, Shield, UserCircle, Package, BarChart3, Ticket
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

// Types
import { UserPage, AdminPage, AdminTab, SystemAdminTab, CartItem, MenuItem, Order } from './types'

// Mock data
import { MOCK_MENU_ITEMS, MOCK_ORDERS, MOCK_STORE_SETTINGS, MOCK_OPERATING_HOURS, DEMO_ACCOUNTS } from './data/mock-data'

// Store admin components
import OrdersManagement from './components/store-admin/OrdersManagement'
import MenuManagement from './components/store-admin/MenuManagement'
import MembersManagement from './components/store-admin/MembersManagement'
import CouponsManagement from './components/store-admin/CouponsManagement'
import InventoryManagement from './components/store-admin/InventoryManagement'
import RobotDeliveryManagement from './components/store-admin/RobotDeliveryManagement'
import SalesAnalytics from './components/store-admin/SalesAnalytics'
import SettlementManagement from './components/store-admin/SettlementManagement'
import OperationSettings from './components/store-admin/OperationSettings'
import AdminAccountsManagement from './components/store-admin/AdminAccountsManagement'

// System admin components
import StoreManagement from './components/system-admin/StoreManagement'
import SystemAdminManagement from './components/system-admin/SystemAdminManagement'
import SystemSettings from './components/system-admin/SystemSettings'

// ============================================================
// 메인 데모 컴포넌트
// ============================================================

export default function CoffeeOrderDemo() {
  // 전역 상태
  const [viewMode, setViewMode] = useState<'user' | 'admin'>('user')
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isSystemAdmin, setIsSystemAdmin] = useState(false)
  const [currentUserName, setCurrentUserName] = useState('')

  // 사용자 화면 상태
  const [userPage, setUserPage] = useState<UserPage>('menu')
  const [cartItems, setCartItems] = useState<CartItem[]>([])
  const [lastOrderNumber, setLastOrderNumber] = useState('')
  const [showScheduleExpanded, setShowScheduleExpanded] = useState(false)

  // 관리자 화면 상태
  const [adminPage, setAdminPage] = useState<AdminPage>('login')
  const [adminTab, setAdminTab] = useState<AdminTab>('orders')
  const [systemAdminTab, setSystemAdminTab] = useState<SystemAdminTab>('stores')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // 장바구니 기능
  const addToCart = (menuItem: MenuItem) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === menuItem.id)
      if (existing) {
        return prev.map(item =>
          item.id === menuItem.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      }
      return [...prev, { ...menuItem, quantity: 1 }]
    })
  }

  const removeFromCart = (id: string) => {
    setCartItems(prev => prev.filter(item => item.id !== id))
  }

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity === 0) {
      removeFromCart(id)
    } else {
      setCartItems(prev => prev.map(item => item.id === id ? { ...item, quantity } : item))
    }
  }

  const getTotalCartItems = () => cartItems.reduce((sum, item) => sum + item.quantity, 0)
  const getTotalAmount = () => cartItems.reduce((sum, item) => sum + item.discountedPrice * item.quantity, 0)

  // 주문 처리
  const handleOrder = () => {
    const orderNumber = `ORD-${String(Date.now()).slice(-6)}`
    setLastOrderNumber(orderNumber)
    setCartItems([])
    setUserPage('order-complete')
  }

  // 로그인 처리
  const handleLogin = (email: string, password: string) => {
    if (email === DEMO_ACCOUNTS.storeAdmin.email && password === DEMO_ACCOUNTS.storeAdmin.password) {
      setIsLoggedIn(true)
      setIsSystemAdmin(false)
      setCurrentUserName(DEMO_ACCOUNTS.storeAdmin.name)
      setAdminPage('dashboard')
      return true
    }
    if (email === DEMO_ACCOUNTS.systemAdmin.email && password === DEMO_ACCOUNTS.systemAdmin.password) {
      setIsLoggedIn(true)
      setIsSystemAdmin(true)
      setCurrentUserName(DEMO_ACCOUNTS.systemAdmin.name)
      setAdminPage('dashboard')
      return true
    }
    return false
  }

  const handleLogout = () => {
    setIsLoggedIn(false)
    setIsSystemAdmin(false)
    setCurrentUserName('')
    setAdminPage('login')
    // 로그아웃 후 관리자 로그인 화면에 남아있음
  }


  // 사용자 화면 렌더링
  if (viewMode === 'user') {
    return (
      <div className="min-h-screen bg-gray-50 light" data-theme="light" style={{ colorScheme: 'light' }}>
        {userPage === 'menu' && (
          <UserMenuPage
            cartItems={cartItems}
            onAddToCart={addToCart}
            onGoToCart={() => setUserPage('cart')}
            onGoToHistory={() => setUserPage('order-history')}
            onGoToAdmin={() => {
              setViewMode('admin')
              if (!isLoggedIn) setAdminPage('login')
            }}
            showScheduleExpanded={showScheduleExpanded}
            setShowScheduleExpanded={setShowScheduleExpanded}
          />
        )}
        {userPage === 'cart' && (
          <UserCartPage
            cartItems={cartItems}
            onBack={() => setUserPage('menu')}
            onRemoveItem={removeFromCart}
            onUpdateQuantity={updateQuantity}
            onOrder={() => setUserPage('order')}
          />
        )}
        {userPage === 'order' && (
          <UserOrderPage
            cartItems={cartItems}
            totalAmount={getTotalAmount()}
            onBack={() => setUserPage('cart')}
            onSubmitOrder={handleOrder}
          />
        )}
        {userPage === 'order-complete' && (
          <UserOrderCompletePage
            orderNumber={lastOrderNumber}
            onHome={() => setUserPage('menu')}
          />
        )}
        {userPage === 'order-history' && (
          <UserOrderHistoryPage
            orders={MOCK_ORDERS}
            onBack={() => setUserPage('menu')}
          />
        )}
      </div>
    )
  }

  // 관리자 화면 렌더링
  return (
    <div className="min-h-screen bg-gray-50 light" data-theme="light" style={{ colorScheme: 'light' }}>
      {adminPage === 'login' && (
        <AdminLoginPage
          onLogin={handleLogin}
          onBackToUser={() => {
            setViewMode('user')
            setUserPage('menu')
          }}
        />
      )}
      {adminPage === 'dashboard' && isLoggedIn && (
        isSystemAdmin ? (
          <SystemAdminDashboard
            activeTab={systemAdminTab}
            setActiveTab={setSystemAdminTab}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
            onLogout={handleLogout}
            onBackToUser={() => {
              setViewMode('user')
              setUserPage('menu')
            }}
          />
        ) : (
          <StoreAdminDashboard
            activeTab={adminTab}
            setActiveTab={setAdminTab}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
            onLogout={handleLogout}
          />
        )
      )}
    </div>
  )
}

// ============================================================
// 사용자 화면 컴포넌트들
// ============================================================

function UserMenuPage({
  cartItems,
  onAddToCart,
  onGoToCart,
  onGoToHistory,
  onGoToAdmin,
  showScheduleExpanded,
  setShowScheduleExpanded
}: {
  cartItems: CartItem[]
  onAddToCart: (item: MenuItem) => void
  onGoToCart: () => void
  onGoToHistory: () => void
  onGoToAdmin: () => void
  showScheduleExpanded: boolean
  setShowScheduleExpanded: (v: boolean) => void
}) {
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0)
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  const handleAddToCart = (item: MenuItem) => {
    onAddToCart(item)
    setToastMessage(`${item.name}이(가) 장바구니에 추가되었습니다`)
    setTimeout(() => setToastMessage(null), 2000)
  }

  return (
    <div className="max-w-sm mx-auto bg-white min-h-screen">
      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg animate-in fade-in slide-in-from-top-2">
          {toastMessage}
        </div>
      )}

      {/* Top Navigation */}
      <div className="flex justify-between items-center px-4 py-2">
        <button
          onClick={onGoToAdmin}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white text-xs font-medium rounded-md transition-colors animate-blink"
        >
          <Settings className="w-3.5 h-3.5" />
          관리자 모드
        </button>
        <style jsx>{`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.2; }
          }
          .animate-blink {
            animation: blink 2s ease-in-out infinite;
          }
        `}</style>
        <div className="flex gap-3">
          <div className="relative cursor-pointer" onClick={onGoToHistory}>
            <FileText className="w-6 h-6 text-gray-900" />
          </div>
          <div className="relative cursor-pointer" onClick={onGoToCart}>
            <ShoppingCart className="w-6 h-6 text-gray-900" />
            {totalItems > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Visual Section */}
      <div className="relative h-60 overflow-hidden bg-gradient-to-br from-amber-100 to-orange-200">
        <img
          src="/demos/coffee-order/store-1.png"
          alt="매장 이미지"
          className="w-full h-full object-cover"
        />
        {MOCK_STORE_SETTINGS.use_robot_delivery && (
          <div className="absolute top-4 left-4 bg-[#191919] text-[#f2f4f6] px-1.5 py-1 rounded-[2px] text-xs font-medium">
            Robot Delivery
          </div>
        )}
      </div>

      {/* Store Info */}
      <div className="px-4 pt-4 pb-5">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <h2 className="text-[#191919] text-lg font-bold">{MOCK_STORE_SETTINGS.store_name}</h2>
            <button className="flex items-center gap-0.5 px-2 py-[5px] border border-[#e0e0e0] rounded-full">
              <Phone className="w-4 h-4 text-gray-600" />
              <span className="text-xs text-gray-600">전화하기</span>
            </button>
          </div>
          <p className="text-[15px] text-gray-600 font-semibold">{MOCK_STORE_SETTINGS.store_location}</p>

          <button
            className="flex items-center justify-between w-full"
            onClick={() => setShowScheduleExpanded(!showScheduleExpanded)}
          >
            <div className="flex items-center gap-[6px]">
              <span className="text-[#26a687] font-semibold text-[15px]">로봇 배송 서비스</span>
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span className="text-[#191919] font-semibold text-[15px]">운영중</span>
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span className="text-[#191919] text-[15px]">09:00~22:00</span>
            </div>
            {showScheduleExpanded ? (
              <ChevronUp className="w-5 h-5 text-[#191919]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#191919]" />
            )}
          </button>

          {showScheduleExpanded && (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
              {Object.entries(MOCK_OPERATING_HOURS).map(([day, hours]) => (
                <div key={day} className="flex justify-between text-[14px] text-gray-600">
                  <span>{day === 'monday' ? '월요일' : day === 'tuesday' ? '화요일' : day === 'wednesday' ? '수요일' : day === 'thursday' ? '목요일' : day === 'friday' ? '금요일' : day === 'saturday' ? '토요일' : '일요일'}</span>
                  <span>{hours.is_open ? `${hours.open_time} ~ ${hours.close_time}` : '휴무'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200"></div>

      {/* Menu Items */}
      <div className="px-4 pt-10 space-y-5 pb-8">
        {MOCK_MENU_ITEMS.filter(item => item.status === '판매중').map(item => (
          <div key={item.id} className="flex items-center gap-5">
            <div className="w-16 h-16 bg-[#f2f4f6] rounded-[4px] overflow-hidden flex-shrink-0 flex items-center justify-center">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 flex items-center justify-between">
              <div className="flex flex-col">
                <h3 className="text-[15px] font-semibold text-[#191919]">{item.name}</h3>
                {item.discount > 0 ? (
                  <>
                    <div className="flex items-center gap-1">
                      <span className="text-[14px] text-[#999999] line-through">{item.originalPrice.toLocaleString()}원</span>
                      <span className="text-[14px] font-semibold text-[#f96960]">{item.discount}%</span>
                    </div>
                    <p className="text-[15px] font-semibold text-[#191919]">{item.discountedPrice.toLocaleString()}원</p>
                  </>
                ) : (
                  <p className="text-[15px] font-semibold text-[#191919]">{item.originalPrice.toLocaleString()}원</p>
                )}
              </div>
              <button
                className="w-6 h-6 rounded-full border-[1.2px] border-[#e0e0e0] flex items-center justify-center hover:bg-gray-100"
                onClick={() => handleAddToCart(item)}
              >
                <Plus className="w-3 h-3 text-[#191919]" strokeWidth={2} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Business Info */}
      <div className="px-4 pb-4">
        <Card className="bg-gray-50 p-4 rounded-lg shadow-sm">
          <div className="space-y-2 text-[14px]">
            <div className="flex items-center">
              <span className="text-gray-600 w-24 flex-shrink-0">사업자명</span>
              <span className="text-[#191919] font-medium">{MOCK_STORE_SETTINGS.business_name}</span>
            </div>
            <div className="flex items-center">
              <span className="text-gray-600 w-24 flex-shrink-0">등록번호</span>
              <span className="text-[#191919] font-medium">{MOCK_STORE_SETTINGS.business_number}</span>
            </div>
            <div className="flex items-center">
              <span className="text-gray-600 w-24 flex-shrink-0">주소</span>
              <div className="flex items-center gap-2 flex-1">
                <span className="text-[#191919] font-medium flex-1">{MOCK_STORE_SETTINGS.business_address}</span>
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

function UserCartPage({
  cartItems,
  onBack,
  onRemoveItem,
  onUpdateQuantity,
  onOrder
}: {
  cartItems: CartItem[]
  onBack: () => void
  onRemoveItem: (id: string) => void
  onUpdateQuantity: (id: string, quantity: number) => void
  onOrder: () => void
}) {
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0)
  const totalAmount = cartItems.reduce((sum, item) => sum + item.discountedPrice * item.quantity, 0)

  return (
    <div className="max-w-sm mx-auto bg-white min-h-screen">
      {/* Header */}
      <div className="relative flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <button onClick={onBack} className="p-0 hover:opacity-70">
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <h1 className="absolute left-1/2 transform -translate-x-1/2 text-lg font-semibold">장바구니</h1>
        <div className="w-6 h-6"></div>
      </div>

      {cartItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-4 pt-32">
          <div className="mb-6 flex flex-col items-center">
            <div className="w-[63px] h-[63px] rounded-full border-[2px] border-[#1a1a1b] flex items-center justify-center mb-3">
              <ShoppingCart className="w-8 h-8 text-[#1a1a1b]" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-[#1a1a1b] mb-2">장바구니가 비어있습니다</h2>
          <p className="text-sm text-gray-500 mb-8">마음에 드는 메뉴를 담아주세요</p>
          <Button variant="outline" onClick={onBack} className="px-12 py-2">
            메뉴 추가
          </Button>
        </div>
      ) : (
        <>
          <div className="px-4">
            {cartItems.map(item => (
              <div key={item.id} className="relative py-6 border-b border-gray-100">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveItem(item.id)}
                  className="absolute top-4 right-0 text-gray-400 hover:text-red-500 w-8 h-8"
                >
                  <X className="w-4 h-4" />
                </Button>

                <div className="flex items-start gap-3 pr-10">
                  <div className="w-16 h-16 bg-gray-100 rounded-full overflow-hidden flex items-center justify-center">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium mb-1">{item.name}</h3>
                    {item.discount > 0 ? (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-gray-400 line-through text-sm">{item.originalPrice.toLocaleString()}원</span>
                          <span className="text-orange-500 font-bold text-sm">{item.discount}%</span>
                        </div>
                        <p className="font-bold text-lg mb-3">{item.discountedPrice.toLocaleString()}원</p>
                      </>
                    ) : (
                      <p className="font-bold text-lg mb-3">{item.originalPrice.toLocaleString()}원</p>
                    )}

                    <div className="flex items-center border rounded-lg w-fit">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onUpdateQuantity(item.id, Math.max(0, item.quantity - 1))}
                        className="w-8 h-8"
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="px-3 py-1 min-w-[2rem] text-center">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 py-4">
            <Button variant="outline" onClick={onBack} className="w-full py-3">
              <Plus className="w-4 h-4 mr-2" />
              메뉴 추가
            </Button>
          </div>

          <div className="bg-gray-100 px-4 py-4">
            <h3 className="font-bold mb-2">유의사항</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p>• 메뉴 사진은 연출된 이미지로 실제와 다를 수 있습니다.</p>
              <p>• 가격은 변동될 수 있습니다.</p>
            </div>
          </div>

          <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-sm bg-white border-t px-4 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-base font-medium text-gray-700">{totalItems}개</span>
              <span className="text-xl font-bold text-gray-900">{totalAmount.toLocaleString()}원</span>
            </div>
            <Button className="w-full bg-[#191919] hover:bg-gray-800 text-white py-6 text-base font-semibold" onClick={onOrder}>
              주문하기
            </Button>
          </div>

          <div className="h-32"></div>
        </>
      )}
    </div>
  )
}

function UserOrderPage({
  cartItems,
  totalAmount,
  onBack,
  onSubmitOrder
}: {
  cartItems: CartItem[]
  totalAmount: number
  onBack: () => void
  onSubmitOrder: () => void
}) {
  const [deliveryMethod, setDeliveryMethod] = useState<'robot' | 'pickup'>('robot')
  const [paymentMethod, setPaymentMethod] = useState('card')
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <div className="max-w-sm mx-auto bg-white min-h-screen">
      {/* Header */}
      <div className="relative flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <button onClick={onBack} className="p-0 hover:opacity-70">
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <h1 className="absolute left-1/2 transform -translate-x-1/2 text-lg font-semibold">주문하기</h1>
        <div className="w-6 h-6"></div>
      </div>

      {/* 수령 방법 */}
      <div className="px-4 py-4">
        <h2 className="font-bold text-lg mb-4">수령 방법</h2>
        <div className="flex gap-3">
          <button
            onClick={() => setDeliveryMethod('robot')}
            className={`flex-1 p-4 border rounded-lg ${deliveryMethod === 'robot' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 ${deliveryMethod === 'robot' ? 'border-blue-500' : 'border-gray-300'}`}>
                {deliveryMethod === 'robot' && <div className="w-3 h-3 bg-blue-500 rounded-full m-0.5"></div>}
              </div>
              <span className={`font-medium ${deliveryMethod === 'robot' ? 'text-blue-500' : ''}`}>로봇 배송</span>
            </div>
          </button>
          <button
            onClick={() => setDeliveryMethod('pickup')}
            className={`flex-1 p-4 border rounded-lg ${deliveryMethod === 'pickup' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 ${deliveryMethod === 'pickup' ? 'border-blue-500' : 'border-gray-300'}`}>
                {deliveryMethod === 'pickup' && <div className="w-3 h-3 bg-blue-500 rounded-full m-0.5"></div>}
              </div>
              <span className={`font-medium ${deliveryMethod === 'pickup' ? 'text-blue-500' : ''}`}>매장 픽업</span>
            </div>
          </button>
        </div>
      </div>

      {/* 도착장소 */}
      <div className="px-4 py-4">
        <h2 className="font-bold text-lg mb-4">도착장소<span className="text-red-500">*</span></h2>
        <div className="w-full p-4 border border-gray-300 rounded-lg bg-gray-50">
          <p className="text-gray-900 font-medium text-lg">
            {deliveryMethod === 'robot' ? '3층 회의실' : '카운터 앞'}
          </p>
        </div>
      </div>

      {/* 결제 수단 */}
      <div className="px-4 py-4 border-t border-gray-100">
        <h2 className="font-bold text-lg mb-4">결제 수단</h2>
        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="결제 수단을 선택하세요" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="card">신용카드</SelectItem>
            <SelectItem value="kakao">카카오페이</SelectItem>
            <SelectItem value="naver">네이버페이</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* 결제금액 */}
      <div className="px-4 py-4 border-t border-gray-100">
        <h2 className="font-bold text-lg mb-4">결제금액</h2>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-gray-700">주문금액 ({totalItems}개)</span>
            <span className="font-medium">{totalAmount.toLocaleString()}원</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="font-bold text-lg">결제금액</span>
            <span className="font-bold text-lg">{totalAmount.toLocaleString()}원</span>
          </div>
        </div>
      </div>

      {/* 하단 버튼 */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-sm bg-white border-t px-4 py-4">
        <Button
          className="w-full bg-[#191919] hover:bg-gray-800 text-white py-6 text-base font-semibold"
          onClick={onSubmitOrder}
        >
          결제하기
        </Button>
      </div>

      <div className="h-24"></div>
    </div>
  )
}

function UserOrderCompletePage({
  orderNumber,
  onHome
}: {
  orderNumber: string
  onHome: () => void
}) {
  return (
    <div className="max-w-sm mx-auto bg-white min-h-screen">
      <div className="px-4 py-4 border-b border-gray-100">
        <h1 className="text-center text-lg font-semibold">주문 완료</h1>
      </div>

      <div className="flex justify-center pt-12 pb-8">
        <div className="relative">
          <CheckCircle className="w-24 h-24 text-[#26a687]" />
        </div>
      </div>

      <div className="px-4 text-center mb-8">
        <h2 className="text-[#191919] text-2xl font-bold mb-2">주문이 완료되었습니다!</h2>
        <p className="text-gray-600 mb-2">
          주문번호: <span className="text-[#191919] font-semibold text-lg">{orderNumber}</span>
        </p>
      </div>

      <div className="mx-4 mb-6 p-4 border border-gray-100 rounded-[4px]">
        <h3 className="text-[#191919] font-semibold mb-3">주문 정보</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">수령 방법</span>
            <span className="font-medium">로봇 배송</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">배송 위치</span>
            <span className="font-medium">3층 회의실</span>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-sm bg-white border-t px-4 py-4">
        <Button
          className="w-full bg-[#191919] hover:bg-gray-800 text-white flex items-center justify-center gap-2"
          onClick={onHome}
        >
          <Home className="w-4 h-4" />
          홈으로
        </Button>
      </div>

      <div className="h-20"></div>
    </div>
  )
}

function UserOrderHistoryPage({
  orders,
  onBack
}: {
  orders: Order[]
  onBack: () => void
}) {
  const [filter, setFilter] = useState<string>('all')

  const getStatusConfig = (status: Order['status']) => {
    const configs = {
      received: { label: '주문접수', bgColor: 'bg-teal-100', textColor: 'text-teal-800' },
      preparing: { label: '준비중', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      delivering: { label: '배송중', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
      pickup_waiting: { label: '픽업대기', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
      completed: { label: '완료', bgColor: 'bg-purple-100', textColor: 'text-purple-800' },
      cancelled: { label: '취소됨', bgColor: 'bg-red-100', textColor: 'text-red-800' }
    }
    return configs[status]
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const month = date.getMonth() + 1
    const day = date.getDate()
    const hours = date.getHours()
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const ampm = hours >= 12 ? '오후' : '오전'
    const displayHours = hours % 12 || 12
    return `${month}월 ${day}일 ${ampm} ${displayHours}:${minutes}`
  }

  const filteredOrders = filter === 'all' ? orders : orders.filter(o => o.status === filter)

  return (
    <div className="max-w-sm mx-auto bg-white min-h-screen">
      <div className="relative flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <button onClick={onBack} className="p-0 hover:opacity-70">
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <h1 className="absolute left-1/2 transform -translate-x-1/2 text-lg font-semibold">주문 내역</h1>
        <div className="w-6 h-6"></div>
      </div>

      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex gap-2 overflow-x-auto">
          {['all', 'received', 'preparing', 'completed'].map(f => (
            <Button
              key={f}
              variant="outline"
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-gray-50 text-gray-600 border-gray-600' : 'text-gray-500 border-gray-300'}
            >
              {f === 'all' ? '전체' : f === 'received' ? '주문접수' : f === 'preparing' ? '준비중' : '완료'}
            </Button>
          ))}
        </div>
      </div>

      <div className="px-4 py-4">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <Coffee className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">주문 내역이 없습니다.</p>
          </div>
        ) : (
          <div>
            {filteredOrders.map(order => {
              const status = getStatusConfig(order.status)
              return (
                <div key={order.id} className="border-b border-gray-100 py-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-[#191919] font-semibold text-lg">주문번호 {order.order_number}</h3>
                      <p className="text-[14px] text-gray-600">{formatDate(order.order_time)}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-[4px] ${status.bgColor}`}>
                      <span className={`text-[13px] font-semibold ${status.textColor}`}>{status.label}</span>
                    </div>
                  </div>

                  <div className="space-y-1 mb-3">
                    {order.items.slice(0, 2).map(item => (
                      <p key={item.id} className="text-[14px] text-gray-600">
                        {item.name} x {item.quantity}
                      </p>
                    ))}
                    {order.items.length > 2 && (
                      <p className="text-[14px] text-gray-500">외 {order.items.length - 2}개</p>
                    )}
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                    <span className="text-[14px] text-gray-600">총 결제금액</span>
                    <span className="text-[#191919] font-semibold text-lg">{order.total_amount.toLocaleString()}원</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================
// 관리자 로그인 페이지
// ============================================================

function AdminLoginPage({ onLogin, onBackToUser }: { onLogin: (email: string, password: string) => boolean; onBackToUser: () => void }) {
  // 초기값으로 점포 관리자 계정 표시
  const [email, setEmail] = useState('store@demo.com')
  const [password, setPassword] = useState('demo1234')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!onLogin(email, password)) {
      setError('이메일 또는 비밀번호가 올바르지 않습니다.')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-100 p-4">
      <Card className="w-full max-w-md p-8 shadow-lg">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-4">
            <Coffee className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">관리자 로그인</h1>
          <p className="text-gray-600 mt-2">Coffee Order 관리 시스템</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">이메일</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="이메일을 입력하세요"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">비밀번호</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="비밀번호를 입력하세요"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? '숨기기' : '보기'}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-base font-semibold">
            로그인
          </Button>
        </form>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm font-medium text-blue-900 mb-2">데모 계정 안내</p>
          <div className="space-y-2 text-sm text-blue-700">
            <div>
              <p className="font-medium">점포 관리자</p>
              <p>store@demo.com / demo1234</p>
            </div>
            <div>
              <p className="font-medium">시스템 관리자</p>
              <p>admin@demo.com / admin1234</p>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Button variant="outline" onClick={onBackToUser} className="w-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            사용자 화면으로 돌아가기
          </Button>
        </div>
      </Card>
    </div>
  )
}

// ============================================================
// 점포 관리자 대시보드
// ============================================================

function StoreAdminDashboard({
  activeTab,
  setActiveTab,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  onLogout,
}: {
  activeTab: AdminTab
  setActiveTab: (tab: AdminTab) => void
  isMobileMenuOpen: boolean
  setIsMobileMenuOpen: (open: boolean) => void
  onLogout: () => void
}) {
  // 원본과 동일한 메뉴 구조 (10개)
  const menuItems = [
    { id: 'orders', label: '주문', icon: Receipt },
    { id: 'menu', label: '메뉴', icon: Coffee },
    { id: 'members', label: '회원', icon: UserCircle },
    { id: 'coupons', label: '쿠폰', icon: Ticket },
    { id: 'inventory', label: '재고', icon: Package },
    { id: 'robot', label: '로봇 배송', icon: Bot },
    { id: 'sales', label: '통계', icon: BarChart3 },
    { id: 'settlement', label: '정산', icon: Calculator },
    { id: 'settings', label: '설정', icon: Settings },
    { id: 'accounts', label: '관리자', icon: Shield },
  ] as const

  const renderContent = () => {
    switch (activeTab) {
      case 'orders':
        return <OrdersManagement />
      case 'menu':
        return <MenuManagement />
      case 'members':
        return <MembersManagement />
      case 'coupons':
        return <CouponsManagement />
      case 'inventory':
        return <InventoryManagement />
      case 'robot':
        return <RobotDeliveryManagement />
      case 'sales':
        return <SalesAnalytics />
      case 'settlement':
        return <SettlementManagement />
      case 'settings':
        return <OperationSettings />
      case 'accounts':
        return <AdminAccountsManagement />
      default:
        return <OrdersManagement />
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="xl:hidden">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px]">
                  <SheetHeader>
                    <SheetTitle>Coffee Order</SheetTitle>
                    <SheetDescription>점포 관리 메뉴</SheetDescription>
                  </SheetHeader>
                  <nav className="flex flex-col gap-2 mt-6">
                    {menuItems.map(item => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveTab(item.id as AdminTab)
                          setIsMobileMenuOpen(false)
                        }}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          activeTab === item.id
                            ? 'bg-amber-100 text-amber-700'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    ))}
                  </nav>
                </SheetContent>
              </Sheet>

              <h1 className="text-base sm:text-xl font-bold text-gray-900 whitespace-nowrap">관리자</h1>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded-full whitespace-nowrap">총괄</span>
              <Button variant="ghost" size="sm" onClick={onLogout} className="flex items-center gap-1 text-gray-600 px-2">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 탭 네비게이션 - 원본 스타일 */}
        <nav className="hidden xl:flex gap-2 mb-6">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id as AdminTab)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                activeTab === item.id
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </button>
          ))}
        </nav>

        {renderContent()}
      </main>
    </div>
  )
}

// ============================================================
// 시스템 관리자 대시보드
// ============================================================

function SystemAdminDashboard({
  activeTab,
  setActiveTab,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  onLogout,
  onBackToUser
}: {
  activeTab: SystemAdminTab
  setActiveTab: (tab: SystemAdminTab) => void
  isMobileMenuOpen: boolean
  setIsMobileMenuOpen: (open: boolean) => void
  onLogout: () => void
  onBackToUser: () => void
}) {
  const menuItems = [
    { id: 'stores', label: '점포 관리', icon: Building2 },
    { id: 'managers', label: '관리자 관리', icon: Users },
    { id: 'settings', label: '시스템 설정', icon: Settings },
  ] as const

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px]">
                  <SheetHeader>
                    <SheetTitle>Coffee Order</SheetTitle>
                    <SheetDescription>시스템 관리 메뉴</SheetDescription>
                  </SheetHeader>
                  <nav className="flex flex-col gap-2 mt-6">
                    {menuItems.map(item => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveTab(item.id as SystemAdminTab)
                          setIsMobileMenuOpen(false)
                        }}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          activeTab === item.id
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    ))}
                  </nav>
                </SheetContent>
              </Sheet>

              <Coffee className="h-8 w-8 text-amber-600" />
              <div>
                <h1 className="text-xl font-semibold">시스템 관리자</h1>
                <p className="text-sm text-muted-foreground hidden sm:block">Coffee Order 통합 관리</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onBackToUser} className="flex items-center gap-2">
                <Coffee className="h-4 w-4" />
                <span className="hidden sm:inline">주문 화면</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={onLogout} className="flex items-center gap-2">
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">로그아웃</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SystemAdminTab)} className="space-y-6">
          <TabsList className="hidden md:grid w-full max-w-md grid-cols-3">
            {menuItems.map(item => (
              <TabsTrigger key={item.id} value={item.id} className="flex items-center gap-2">
                <item.icon className="h-4 w-4" />
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="stores">
            <StoreManagement />
          </TabsContent>
          <TabsContent value="managers">
            <SystemAdminManagement />
          </TabsContent>
          <TabsContent value="settings">
            <SystemSettings />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  )
}
